import { Trans } from '@lingui/react';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Option } from '@startuptools/ui/Option';
import { Select } from '@startuptools/ui/Select';
import { type Control, Controller, type FormState, useWatch } from 'react-hook-form';
import { DateTime } from 'luxon';
import {
  type GqlShareTransactionGroup,
  type GqlSnrShareCapitalChange,
  GqlTransactionKind,
} from '../../../../graphql/base-types.graphql';
import { type StgFormData } from './TransactionGroupForm';
import { StgLabel } from '../StgLabel';

export const TransactionGroupFormSameDaySts = ({
  control,
  formState,
  shareTransactionGroups,
  snrShareCapitalChanges,
}: {
  control: Control<StgFormData>;
  formState: FormState<StgFormData>;
  shareTransactionGroups: (Pick<
    GqlShareTransactionGroup,
    'transactionDate' | 'kind' | 'snrShareCapitalChangeId' | 'id'
  > & { snrShareCapitalChange?: Pick<GqlSnrShareCapitalChange, 'name'> | null })[];
  snrShareCapitalChanges: Pick<
    GqlSnrShareCapitalChange,
    'id' | 'name' | 'filingNumber' | 'serialNumber' | 'filingYear'
  >[];
}) => {
  const date = useWatch({ control, name: 'date' });
  const kind = useWatch({ control, name: 'kind' });
  const stgsOnDate = shareTransactionGroups.filter(stg => {
    if (stg.snrShareCapitalChange?.name === 'NYBILDNING') {
      // Never show foundation as an option
      return false;
    }
    if (
      kind === 'next' && [
        GqlTransactionKind.Issue,
        GqlTransactionKind.Merge,
        GqlTransactionKind.Issue,
        GqlTransactionKind.Unissue,
      ]
    ) {
      // Never allow it to be added before the previous one in case
      return false;
    }
    return DateTime.fromISO(stg.transactionDate).startOf('day').toMillis() === date.startOf('day').toMillis();
  });
  const firstStg = stgsOnDate?.[0];
  if (!firstStg) {
    return null;
  }

  const loading = false;

  return (
    <Controller
      name="beforeStgId"
      control={control}
      render={({ field: { value, onChange, ...field } }) => (
        <FormControl>
          <FormLabel>
            <Trans id="Before or after transaction on same day" />
          </FormLabel>

          <Select
            {...field}
            value={value}
            defaultValue={firstStg?.id + 1}
            color={formState.errors.beforeStgId ? 'danger' : undefined}
            name="sortOrder"
            onChange={(_event, value) => {
              onChange(value);
            }}
            slotProps={{ listbox: { sx: { zIndex: 999999 } } }}
            disabled={loading}
          >
            <Option value={''}>
              <Trans
                id="After {label}"
                values={{
                  label: (
                    <StgLabel
                      stg={firstStg}
                      snrScc={snrShareCapitalChanges.find(scc => scc.id === firstStg.snrShareCapitalChangeId)}
                    />
                  ),
                }}
              />
            </Option>
            {stgsOnDate.map(stg => {
              return (
                <Option value={stg.id} key={stg.id}>
                  <Trans
                    id="Before {label}"
                    values={{
                      label: (
                        <StgLabel
                          stg={stg}
                          snrScc={snrShareCapitalChanges.find(scc => scc.id === stg.snrShareCapitalChangeId)}
                        />
                      ),
                    }}
                  />
                </Option>
              );
            })}
          </Select>
        </FormControl>
      )}
    />
  );
};
